import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import AppShell from "../components/AppShell";
import { Logo } from "../components/Logo";
import OtpInput from "../components/OtpInput";
import { verifyOtp, resendOtp } from "../lib/api";
import { saveAuth } from "../lib/auth";

const RESEND_COOLDOWN = 30;

export default function VerifyOtp() {
  const loc = useLocation();
  const nav = useNavigate();
  const state = loc.state || {};
  const phone = state.phone;
  const purpose = state.purpose || "signup";

  const OTP_LENGTH = 4;
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);

  useEffect(() => {
    if (!phone) {
      nav("/signup", { replace: true });
      return;
    }
    const t = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [phone, nav]);

  const submit = async () => {
    if (otp.length !== OTP_LENGTH) {
      toast.error(`Enter the ${OTP_LENGTH}-digit code`);
      return;
    }
    setLoading(true);
    try {
      const res = await verifyOtp({ phone, otp, purpose });
      if (purpose === "signup") {
        saveAuth(res.token, res.user);
        toast.success("Account verified");
        nav("/payment", { replace: true });
      } else {
        nav("/reset-password", { state: { phone, otp }, replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const doResend = async () => {
    if (countdown > 0) return;
    try {
      await resendOtp({ phone, purpose });
      setCountdown(RESEND_COOLDOWN);
      toast.success("Code resent", { description: `SMS sent to ${phone}` });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend");
    }
  };

  return (
    <AppShell>
      <div className="min-h-screen bg-white flex flex-col">
        <div className="screen flex-1 flex flex-col">
          <button
            onClick={() => nav(-1)}
            data-testid="otp-back-btn"
            className="w-10 h-10 rounded-full border border-neutral-200 flex items-center justify-center hover:bg-neutral-50"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          <div className="mt-8 flex justify-center">
            <Logo size="md" />
          </div>

          {purpose === "signup" && (
            <div className="mt-6 text-center" data-testid="otp-step-badge">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFF0E6] text-brand text-[10.5px] font-bold tracking-[0.14em] uppercase">
                Step 4 of 4
              </div>
            </div>
          )}

          <div className="mt-4 text-center">
            <h1 className="font-display font-bold text-[26px] tracking-tight text-black" data-testid="otp-title">
              Verify your number
            </h1>
            <p className="mt-2 text-[13px] text-neutral-500">
              Code sent to <span className="font-semibold text-black">{phone}</span>
            </p>
          </div>

          {/* Demo/dev OTP hint intentionally removed — real SMS only */}

          <div className="mt-8">
            <OtpInput value={otp} onChange={setOtp} length={OTP_LENGTH} />
          </div>

          <div className="mt-8 flex justify-center">
            <button
              onClick={submit}
              disabled={loading || otp.length !== OTP_LENGTH}
              data-testid="otp-verify-btn"
              className={`btn-brand h-11 flex items-center justify-center text-[14px] min-w-[220px] ${
                otp.length === OTP_LENGTH && !loading ? "btn-brand--pulse" : ""
              }`}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify"}
            </button>
          </div>

          <div className="mt-6 text-center text-[13px] text-neutral-500">
            Didn't receive?{" "}
            <button
              onClick={doResend}
              disabled={countdown > 0}
              data-testid="otp-resend-btn"
              className={`font-semibold ${countdown > 0 ? "text-neutral-400 cursor-not-allowed" : "text-brand"}`}
            >
              {countdown > 0 ? `Resend in ${countdown}s` : "Resend code"}
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
